import { CopyableTextAreaHeaderContainer } from "./styles";
import CopyToClipboard from "react-copy-to-clipboard";
import InlineSVG from "react-inlinesvg/esm";
import { useState } from "react";
import { sendInvocationEvent } from "../../../Utils";
import { AppEventPublisher, StoryBibleAction } from "../../../AppEventPublisher";
import { memo, useEffect } from "react";
import { motion } from "framer-motion";
import classNames from "classnames";
import HoverTippable from "../HoverTippable";
import IconButton from "../../IconButton";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import "rc-slider/assets/index.css";
import AnimatedWrapper from "../../AnimatedWrapper";
import DotsActionMenu from "src/components/DotsActionMenu";
import { SudoButton } from "src/sudokit/components/SudoButton";
import StickyNoteAnchor from "../../StickyTour/StickyNoteAnchor";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { TourSignal } from "../../StickyTour/TourTypes";

const Header = ({
  generate,
  generateLabel: _generateLabelOverride,
  generateButtonType: _generateButtonType,
  generateButtonMode: _generateButtonMode,
  generateButtonOpenModal: _generateButtonOpenModal,
  generateDisabled,
  generateDisabledMessage,
  generateSceneDirections: _generateSceneDirections,
  textContent,
  title,
  titleAccessory,
  titleClassName,
  syncing,
  lastInvocation,
  loadAnimationDuration = 6,
  setLastInvocation,
  history,
  isInChapter,
  pausable = false,
  proseModes: _proseModes,
  proseMode,
  setScenes,
  sceneId,
  editor,
  generateProse: _generateProse,
  className,
  type,
  iconPath,
  isFooter = false,
  scene,
  editorRef: _editorRef,
  rightComponents,
  preflight,
  generationWords,
  showHoverIcons,
  showDotsMenu,
  actions,
  collapsed,
  onToggleCollapse,
  dotsMenuAnchorId,
  headerAnchorId,
  headingId,
  /** When Synopsis uses agentic CTAs (`trigger_cta_chat`), legacy analytics emit there — skip duplicate. */
  skipStoryBibleGenerateAnalyticsInvoke,
}) => {
  const requesting = useStoryBibleStore((state) => state.requesting);
  const abortStream = useStoryBibleStore((state) => state.abortStream);
  const outOfQuota = useStoryBibleStore((state) => state.outOfQuota);
  const isStreaming = useStoryBibleStore((state) => state.isStreaming);
  const [, setCopied] = useState(false);
  const [requested, setRequested] = useState(false); // Track if request came from this instance
  const [gradientPercent, setGradientPercent] = useState(0);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  // Clear requested for the instance once requesting is finished
  useEffect(() => {
    if (!requesting) {
      setRequested(false);
    }
  }, [requesting]);

  useEffect(() => {
    if (!isStreaming && requested) {
      setGradientPercent(90);
    } else if (isStreaming && requested) {
      setGradientPercent(100);
    } else if (generationWords?.proseWords === 0) {
      setGradientPercent(0);
      setTimeout(() => {
        setGradientPercent(90);
      }, 50);
    } else if (generationWords?.proseWords > 0) {
      setGradientPercent(90);
    } else {
      setGradientPercent(0);
    }
  }, [isStreaming, requested, generationWords?.proseWords]);

  // Don't allow mid-document continuation when using custom models
  useEffect(() => {
    if (proseMode?.key?.startsWith("CUSTOM_")) {
      setScenes &&
        setScenes((draft) => {
          if (!draft[sceneId]) return;
          draft[sceneId].isContinue = false;
        });
      return;
    }
  }, [proseMode?.key, scene?.isContinue, sceneId, setScenes]);

  const showPause = pausable && requested && requesting && abortStream;

  const pauseDisabled = (showPause && !isStreaming) || proseMode?.key?.startsWith("CUSTOM_"); // Don't allow mid-stream pausing when using custom models

  let pauseLabel = "Pause";
  if (pauseDisabled) {
    pauseLabel = "Preparing...";
  }
  const showCancel = !showPause && requested && requesting && abortStream;

  const hasButton = showCancel || showPause;

  return (
    <CopyableTextAreaHeaderContainer
      isInChapter={isInChapter}
      className={classNames(className, {
        "with-button": hasButton,
        "without-button": !hasButton,
      })}
    >
      {headerAnchorId && <StickyNoteAnchor anchorId={headerAnchorId} />}
      <div className="left-container">
        {onToggleCollapse && (
          <div
            className="chevron-wrapper cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              onToggleCollapse();
            }}
          >
            <InlineSVG
              src="/assets/chevron-down.svg"
              className={classNames("collapse-chevron", { "rotated-chevron": collapsed })}
              aria-label={`${collapsed ? "Expand" : "Collapse"} ${title}`}
            />
          </div>
        )}
        <h2
          id={headingId}
          className={classNames(titleClassName || "title", { "cursor-pointer": onToggleCollapse })}
          style={onToggleCollapse ? { userSelect: "none" } : undefined}
          onClick={
            onToggleCollapse
              ? (e) => {
                  e.preventDefault();
                  onToggleCollapse();
                }
              : undefined
          }
        >
          <InlineSVG
            aria-hidden="true"
            className={classNames({ "generator-icon": type === "commands" })}
            src={iconPath}
          ></InlineSVG>
          <span className="title-label">{title}</span>
          {titleAccessory}
          {syncing && (
            <motion.span
              initial={{ opacity: 0, rotate: 0 }}
              animate={{ opacity: 1, rotate: 360 }}
              transition={{ opacity: { duration: 0.2 }, rotate: { duration: 0.8, repeat: Infinity, ease: "linear" } }}
              style={{
                display: "inline-flex",
                marginLeft: 6,
                width: 12,
                height: 12,
                border: "2px solid var(--grey-2)",
                borderTopColor: "var(--grey-1)",
                borderRadius: "50%",
              }}
            />
          )}
        </h2>
      </div>
      <div
        className={classNames("right-container", {
          footer: isFooter,
        })}
      >
        {!isFooter && (
          <>
            <AnimatedWrapper
              isVisible={showHoverIcons}
              style={{ zIndex: "1" }} // Only way I can prevent the tooltip from getting clipped
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <CopyToClipboard
                text={textContent}
                onCopy={() => {
                  setCopied(true);
                  setTimeout(() => {
                    setCopied(false);
                  }, 1000);
                  if (lastInvocation) {
                    sendInvocationEvent(lastInvocation, "copy");
                  }
                }}
                options={{ format: "text/plain" }}
              >
                <IconButton
                  src="/assets/duplicate.svg"
                  className="copy"
                  disabled={!textContent}
                  ariaLabel="Copy to Clipboard"
                ></IconButton>
              </CopyToClipboard>
            </AnimatedWrapper>

            {history && (
              <AnimatedWrapper
                isVisible={showHoverIcons}
                style={{ zIndex: "1" }}
              >
                <IconButton
                  src="/assets/clock.svg"
                  className="history"
                  disabled={isStreaming || requesting}
                  onClick={(e) => {
                    e.stopPropagation();
                    history(e);
                  }}
                  ariaLabel="Show History"
                />
              </AnimatedWrapper>
            )}

            {showDotsMenu && (
              <AnimatedWrapper
                isVisible={showHoverIcons}
                style={{ zIndex: "1" }}
              >
                <DotsActionMenu
                  smallDots
                  actions={actions}
                  anchorId={dotsMenuAnchorId}
                />
              </AnimatedWrapper>
            )}

            {type === "synopsis" && !textContent?.trim() && generate && !(requested && isStreaming) && (
              <HoverTippable
                generateDisabled={generateDisabled}
                generateDisabledMessage={generateDisabledMessage}
                id="button-container-generate-synopsis-simplified"
              >
                <StickyNoteAnchor anchorId="story_bible_synopsis_generate_button" />
                <SudoButton
                  variant="primary"
                  state={requested && requesting && !isStreaming ? "loading" : "default"}
                  leftIcon={requested && requesting && !isStreaming ? undefined : "/assets/AI-sparkle.svg"}
                  onClick={(e) => {
                    e.stopPropagation();
                    sendFlowSignal(TourSignal.STORY_BIBLE_SYNOPSIS_GENERATE_CLICKED);
                    if (generate()) {
                      setRequested(true);
                      setLastInvocation(null);
                      if (!skipStoryBibleGenerateAnalyticsInvoke) {
                        AppEventPublisher.storyBible.generate(type);
                      }
                    }
                  }}
                  disabled={requesting || outOfQuota || generateDisabled}
                  aria-label="Generate Synopsis"
                >
                  Generate Synopsis
                </SudoButton>
              </HoverTippable>
            )}

            {type === "synopsis" && requested && isStreaming && (
              <SudoButton
                variant="tertiary"
                onClick={(e) => {
                  e.stopPropagation();
                  AppEventPublisher.storyBible.action(type, StoryBibleAction.CancelStream);
                  abortStream();
                  sendInvocationEvent(lastInvocation, "cancel");
                }}
                aria-label="Cancel Synopsis Generation"
              >
                Cancel
              </SudoButton>
            )}
          </>
        )}

        {showCancel && !(type === "synopsis") && (
          <motion.button
            onClick={() => {
              AppEventPublisher.storyBible.action(type, StoryBibleAction.CancelStream);
              abortStream();
              setGradientPercent(0);
              sendInvocationEvent(lastInvocation, "cancel");
            }}
            initial={{
              background: `linear-gradient(90deg,  var(--primary-cta) ${0}%, var(--grey-2) ${0}%)`,
            }}
            animate={{
              background: `linear-gradient(90deg, var(--primary-cta)  ${gradientPercent}%, var(--grey-2) ${gradientPercent}%)`,
              transition: {
                duration: gradientPercent === 0 || gradientPercent === 100 ? 0 : loadAnimationDuration,
                ease: "linear",
              },
            }}
          >
            Cancel
          </motion.button>
        )}

        {showPause && !preflight ? (
          <motion.button
            className="pause"
            onClick={() => {
              abortStream();
              sendInvocationEvent(lastInvocation, "cancel");
              setScenes((draft) => {
                draft[sceneId].isContinue = true;
                draft[sceneId].restartFromIndex = draft[sceneId].lastCommandIndex;
              });
              editor.setOptions({ editable: true });
            }}
            initial={{
              background: `linear-gradient(90deg,  var(--primary-cta) ${0}%, var(--primary-cta-light) ${0}%)`,
            }}
            animate={{
              background: `linear-gradient(90deg, var(--primary-cta)  ${gradientPercent}%, var(--primary-cta-light) ${gradientPercent}%)`,
              transition: {
                duration: gradientPercent === 0 || gradientPercent === 100 ? 0 : loadAnimationDuration,
                ease: "linear",
              },
            }}
            disabled={pauseDisabled}
          >
            {!pauseDisabled && <InlineSVG src="/assets/pause.svg" />}
            <div className="labels">
              <span className="label">{pauseLabel}</span>
            </div>
          </motion.button>
        ) : null}
        {rightComponents}
      </div>
    </CopyableTextAreaHeaderContainer>
  );
};

export default memo(Header);
